import { h, Fragment } from "preact";
import { useState, useEffect, useRef, useMemo } from "preact/hooks";
import useProduct from "../../helpers/useProduct/useProduct";
import unifyGroupOptions from "../../helpers/unifyGroupOptions";
import unifyGroupVariants from "../../helpers/unifyGroupVariants";
import { IdPrefixContext } from "../../helpers/idPrefixContext";
import ProductOption from "./product-option";
import ProductOptionProperties from "./product-option-properties";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import ProductDiscontinued from "./product-discontinued";
import productOptionStockStatus from "./productOptionStockStatus";

const optionValueExists = ({ options, optionName, optionValue }) =>
  options.some(
    (option) =>
      option.optionName === optionName &&
      option.optionValues.some(({ name }) => optionValue === name),
  );

const OPTION_ROLES = {
  primary: "primary",
  secondary: "secondary",
  linked: "linked",
};

export default function ProductOptions({
  productId,
  idPrefix,
  initialVariantId,
  initialVariantHidden,
  initialFirstOptionValue,
  optionsRole = null,
  enableSwatchPreview,
  enableAccordions,
  quantityRequested,
  enableAvailabilityPreview,
  onVariantChanged,
  optionSize = "default",
  disabledSwatchStyle,
  unavailableStockStatuses,
  waitlistStockStatuses,
  expandAllOptions,
}) {
  const [productData] = useProduct(productId);
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedOptionValues, setSelectedOptionValues] = useState({});
  const [disabledOptionValues, setDisabledOptionValues] = useState({});
  const [swatchPreviewIndex, setSwatchPreviewIndex] = useState(0);
  const ref = useRef(null);
  const selectedVariant =
    variants.find((variant) =>
      variant.selectedOptions.every((selectedOption) => {
        return (
          selectedOptionValues[selectedOption.name] === selectedOption.value
        );
      }),
    ) || null;

  const selectedVariantId = selectedVariant?.id;

  const selectedIsHidden =
    selectedVariant && JSON.parse(selectedVariant.hideOnPdp);

  const {
    unavailableOptionValues,
    waitlistOptionValues,
    variantsByOptionValue,
  } = useMemo(() => {
    const newUnavailableOptionValues = {};
    const newWaitlistOptionValues = {};
    const newVariantsByOptionValue = {};

    options.forEach((option) => {
      const newOptionValues = option.optionValues.map((optionValue) => {
        // Find variant that matches this option value and all other selected option values
        // Value available is that variant's stock status

        const optionValuesCombination = {
          ...selectedOptionValues,
          [option.optionName]: optionValue.name,
        };

        const matchingVariant = variants.find((variant) =>
          Object.entries(optionValuesCombination).every(
            ([optionName, optionValue]) =>
              variantHasOptionValue({ variant, optionName, optionValue }),
          ),
        );

        if (!matchingVariant) return false;

        const stockStatus = productOptionStockStatus({
          variant: matchingVariant,
          quantity: quantityRequested,
        });

        return {
          unavailable: unavailableStockStatuses.includes(stockStatus.status),
          waitlist: waitlistStockStatuses.includes(stockStatus.status),
          variant: matchingVariant,
          optionValue: optionValue.name,
        };
      });

      if (enableAvailabilityPreview) {
        newUnavailableOptionValues[option.optionName] = newOptionValues
          .filter(({ unavailable }) => unavailable)
          .map(({ optionValue }) => optionValue);
      }

      newWaitlistOptionValues[option.optionName] = newOptionValues
        .filter(({ waitlist }) => waitlist)
        .map(({ optionValue }) => optionValue);

      newVariantsByOptionValue[option.optionName] = newOptionValues;
    });

    return {
      unavailableOptionValues: newUnavailableOptionValues,
      waitlistOptionValues: newWaitlistOptionValues,
      variantsByOptionValue: newVariantsByOptionValue,
    };
  }, [
    selectedVariantId,
    variants,
    options,
    enableAvailabilityPreview,
    unavailableStockStatuses,
  ]);

  const updateOptionValue = (newValue, optionName, fromLinked) => {
    if (!optionValueExists({ options, optionName, optionValue: newValue }))
      return;

    if (
      optionsRole === OPTION_ROLES.primary ||
      (optionsRole === OPTION_ROLES.linked && !fromLinked)
    ) {
      ref?.current.dispatchEvent(
        new CustomEvent("variant:option-changed", {
          bubbles: true,
          detail: {
            optionName,
            optionValue: newValue,
            optionsRole,
          },
        }),
      );
    }

    const newDisabledOptionValues = {};

    options.forEach((option) => {
      // All values for most recently selected option are valid
      if (option.optionName === optionName) {
        newDisabledOptionValues[option.optionName] = [];
        return;
      }

      newDisabledOptionValues[option.optionName] = option.optionValues
        .filter((optionValue) => {
          return !variants.some((variant) => {
            const hasValue = variantHasOptionValue({
              variant,
              optionName: option.optionName,
              optionValue: optionValue.name,
            });

            const hasLastSelectedValue = variantHasOptionValue({
              variant,
              optionName,
              optionValue: newValue,
            });

            return hasValue && hasLastSelectedValue;
          });
        })
        .map(({ name }) => name);
    });

    setDisabledOptionValues(newDisabledOptionValues);

    const newSelectedOptionValues = {
      ...selectedOptionValues,
    };

    newSelectedOptionValues[optionName] = newValue;
    setSelectedOptionValues(newSelectedOptionValues);
  };

  useEffect(() => {
    const unifiedVariants = unifyGroupVariants(
      productData,
      initialVariantHidden,
    );

    setVariants(unifiedVariants);

    const unifiedOptions = unifyGroupOptions(productData);
    setSwatchPreviewIndex(
      unifiedOptions.findIndex(({ optionValues }) =>
        optionValues.some((optionValue) => optionValue.swatch),
      ),
    );
    setOptions(unifiedOptions);

    let initialVariant = null;

    // Try to find a matching variant (for add on products)
    if (initialFirstOptionValue && unifiedOptions.length) {
      initialVariant = unifiedVariants.find((variant) =>
        variantHasOptionValue({
          variant,
          optionName: unifiedOptions[0].optionName,
          optionValue: initialFirstOptionValue,
        }),
      );
    }

    if (!initialVariant) {
      initialVariant = unifiedVariants.find(
        (variant) => variant.id === initialVariantId,
      );
    }

    if (initialVariant) {
      setSelectedOptionValues(
        Object.fromEntries(
          initialVariant.selectedOptions.map((option) => [
            option.name,
            option.value,
          ]),
        ),
      );
    } else {
      // Select first option value
      setSelectedOptionValues(
        Object.fromEntries(
          unifiedOptions.map((option) => [
            option.optionName,
            option.optionValues[0].name,
          ]),
        ),
      );
    }
  }, [productData, setSelectedOptionValues, initialVariantId]);

  useEffect(() => {
    if (!ref || !variants.length) return;

    const detail = {
      variant: selectedVariant,
      optionsRole,
    };

    if (typeof onVariantChanged === "function") {
      onVariantChanged(detail);
    } else {
      ref.current.dispatchEvent(
        new CustomEvent("variant:changed", {
          bubbles: true,
          detail,
        }),
      );
    }
  }, [selectedVariant, variants, ref]);

  useEffect(() => {
    if (optionsRole === OPTION_ROLES.primary) return;

    const handlePrimaryVariantOptionChange = (e) => {
      if (
        optionsRole === OPTION_ROLES.secondary &&
        e.detail.optionsRole !== OPTION_ROLES.primary
      )
        return;

      if (
        optionsRole === OPTION_ROLES.linked &&
        e.detail.optionsRole !== OPTION_ROLES.linked
      )
        return;

      const { optionName, optionValue } = e.detail;

      updateOptionValue(
        optionValue,
        optionName,
        e.detail.optionsRole === OPTION_ROLES.linked,
      );
    };

    document.addEventListener(
      "variant:option-changed",
      handlePrimaryVariantOptionChange,
    );

    return () =>
      document.removeEventListener(
        "variant:option-changed",
        handlePrimaryVariantOptionChange,
      );
  }, [updateOptionValue, optionsRole]);

  const badge = selectedVariant
    ? selectedVariant.badge || selectedVariant.product.badge
    : null;

  return (
    <div
      className={`product-options ${variants.length ? "product-options--loaded" : ""}`}
      ref={ref}
    >
      {selectedVariant && (
        <input
          type="hidden"
          name="id"
          value={window.gidToId(selectedVariant.id)}
        />
      )}
      {selectedVariant && selectedVariant.retailPrice && (
        <input
          type="hidden"
          name="properties[_retail_price]"
          value={window.formatMoney(selectedVariant.retailPrice, false, "USD")}
        />
      )}
      {selectedVariant && (
        <input
          type="hidden"
          name="properties[_sale_item]"
          value={!!selectedVariant.saleItem}
        />
      )}
      {selectedVariant && selectedVariant.finalSale && (
        <input
          type="hidden"
          name="properties[_final_sale]"
          value={selectedVariant.finalSale}
        />
      )}
      {selectedVariant && (
        <input
          type="hidden"
          name="_product_gid"
          value={selectedVariant.product.id}
        />
      )}
      {selectedVariant && JSON.parse(selectedVariant.madeToOrder) && (
        <input type="hidden" name="properties[_made_to_order]" value="true" />
      )}
      {badge && JSON.parse(badge.showInCart) && (
        <>
          <input type="hidden" name="properties[_badge]" value={badge.text} />
          <input
            type="hidden"
            name="properties[_badge_color]"
            value={badge.color}
          />
          <input
            type="hidden"
            name="properties[_badge_position]"
            value={badge.position}
          />
        </>
      )}
      <ProductOptionProperties variant={selectedVariant} />
      <IdPrefixContext.Provider value={idPrefix}>
        {options.map((option, i) => (
          <ProductOption
            key={option.optionName}
            option={option}
            updateOptionValue={(newValue) =>
              updateOptionValue(newValue, option.optionName)
            }
            selectedOptionValue={selectedOptionValues[option.optionName]}
            unavailableOptionValues={unavailableOptionValues[option.optionName]}
            waitlistOptionValues={waitlistOptionValues[option.optionName]}
            disabledOptionValues={disabledOptionValues[option.optionName]}
            variantsByOptionValue={variantsByOptionValue[option.optionName]}
            allowMaterialGroups={i === 0}
            openByDefault={i === 0 || expandAllOptions}
            last={i === options.length - 1}
            selectedIsHidden={selectedIsHidden}
            selectedVariant={selectedVariant}
            enableSwatchPreview={enableSwatchPreview && i == swatchPreviewIndex}
            enableAccordions={enableAccordions}
            optionSize={optionSize}
            disabledSwatchStyle={disabledSwatchStyle}
          />
        ))}
      </IdPrefixContext.Provider>
      {selectedIsHidden && (
        <ProductDiscontinued product={productData} variants={variants} />
      )}
      {variants.length > 0 && !selectedVariant && (
        <div
          className="product-options__unavailable"
          dangerouslySetInnerHTML={{
            __html: window.localeStrings.product.unavailable_combination,
          }}
        ></div>
      )}
      {variants.length === 0 && (
        <div className="product-options__skeleton"></div>
      )}
    </div>
  );
}
